import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { css, cssMap } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';

import { i18n } from './messages.ts';
import type { AgentCardConfig, AgentCardLayout } from './agentCards.config.ts';

/**
 * Identifies clicks originating from the "Rovo Agents for you" card nudge as the
 * source of the chat-new event, so the Rovo sidebar / analytics can attribute
 * the conversation to this surface.
 */
const TRY_NOW_SOURCE = 'for-you-agent-card-nudge';
const ANALYTICS_SOURCE = 'homeScreen';
const ANALYTICS_ACTION_SUBJECT = 'rovoAgentCard';

const getAnalyticsAttributes = (card: AgentCardConfig) => ({
	cardKey: card.key,
	agentExternalConfigReference: card.agentExternalConfigReference,
});

const cardStyles = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 320px',
	minWidth: 'min(100%, 320px)',
	boxSizing: 'border-box',
	paddingTop: token('space.200'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.200'),
	borderRadius: token('radius.medium'),
	backgroundColor: token('elevation.surface.sunken'),
	// The preview thumbnail bleeds off the bottom edge of the card.
	overflow: 'hidden',
	// Affordances (dismiss + CTA) are hidden by default and revealed on hover or
	// keyboard focus. We drive this via custom properties on the card itself so we
	// never style child elements directly (see affordanceBaseStyles).
	'--rovo-agent-affordance-opacity': 0,
	'--rovo-agent-affordance-events': 'none',
	'&:hover, &:focus-within': {
		'--rovo-agent-affordance-opacity': 1,
		'--rovo-agent-affordance-events': 'auto',
	},
});

const layoutStyles = css({
	flexDirection: 'row',
	alignItems: 'stretch',
	minHeight: '132px',
});

const bodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	minWidth: 0,
	gap: token('space.050'),
});

const bodyLayoutStyles = cssMap({
	oneColumn: {
		flex: '0 1 360px',
		paddingRight: token('space.300'),
	},
	twoColumn: {
		flex: '0 1 268px',
		paddingRight: token('space.200'),
	},
});

const headerRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const iconImageStyles = css({
	display: 'block',
	// Badge SVGs are a hexagon that is slightly taller than wide (~18x20). Fix the
	// height and let the width follow the aspect ratio so it is never squeezed.
	height: '24px',
	width: 'auto',
	flexShrink: 0,
	objectFit: 'contain',
});

const previewWrapperStyles = css({
	position: 'relative',
	flex: '1 1 auto',
	minWidth: 0,
	marginTop: token('space.negative.150'),
	overflow: 'hidden',
});

const previewLayoutStyles = cssMap({
	oneColumn: {
		flexBasis: '381px',
	},
	twoColumn: {
		flexBasis: '250px',
	},
});

const previewImageStyles = css({
	position: 'absolute',
	top: '50%',
	right: token('space.0'),
	display: 'block',
	width: '100%',
	height: 'auto',
	objectFit: 'contain',
	transform: 'translateY(-50%)',
});

const previewImageLayoutStyles = cssMap({
	oneColumn: {
		maxWidth: '381px',
	},
	twoColumn: {
		maxWidth: '250px',
	},
});

const affordanceBaseStyles = css({
	// Visibility is controlled by the card root via custom properties (revealed on
	// card hover / focus-within). Fall back to hidden if the property is unset.
	opacity: 'var(--rovo-agent-affordance-opacity, 0)',
	pointerEvents: 'var(--rovo-agent-affordance-events, none)',
	transitionDuration: '150ms',
	transitionProperty: 'opacity',
	transitionTimingFunction: 'ease-in-out',
	// Keep an affordance visible whenever it is itself focused.
	'&:focus-within': {
		opacity: 1,
		pointerEvents: 'auto',
	},
});

const dismissButtonStyles = css({
	position: 'absolute',
	top: token('space.100'),
	right: token('space.100'),
	zIndex: 1,
});

const ctaRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	alignSelf: 'flex-start',
	gap: token('space.050'),
	marginTop: token('space.150'),
	padding: token('space.0'),
	border: 'none',
	backgroundColor: 'transparent',
	color: token('color.link'),
	font: token('font.body.small'),
	cursor: 'pointer',
	'&:hover': {
		textDecoration: 'underline',
	},
});

export type AgentCardProps = {
	card: AgentCardConfig;
	layout?: AgentCardLayout;
	/**
	 * Called when the user clicks the dismiss control. The parent removes the card
	 * from the visible set. Dismiss persistence is handled separately (PR3).
	 */
	onDismiss?: () => void;
};

/**
 * A single static "Rovo Agents for you" recommendation card.
 *
 * PR2 (CCCEMAU-3070): visuals + responsive layout. The dismiss control removes the
 * card from the current view.
 * PR4 (CCCEMAU-3070): the "Try it" CTA opens the card's OOTB agent in the Rovo
 * right sidebar via a `chat-new` pubsub event, targeting the agent by its stable
 * `agentExternalConfigReference`.
 */
export const AgentCard: FC<AgentCardProps> = ({ card, layout = 'twoColumn', onDismiss }) => {
	const intl = useIntl();
	const publish = usePublish('ai-mate');
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const agentTitle = intl.formatMessage(card.title);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'viewed',
				actionSubject: ANALYTICS_ACTION_SUBJECT,
				source: ANALYTICS_SOURCE,
				attributes: getAnalyticsAttributes(card),
			},
		}).fire();
	}, [card, createAnalyticsEvent]);

	const handleTryNow = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryRovoAgentCardButton',
				source: ANALYTICS_SOURCE,
				attributes: getAnalyticsAttributes(card),
			},
		}).fire();
		publish({
			type: 'chat-new',
			source: TRY_NOW_SOURCE,
			openChatMode: 'sidebar',
			data: {
				name: agentTitle,
				dialogues: [],
				agentExternalConfigReference: card.agentExternalConfigReference,
			},
		});
	}, [createAnalyticsEvent, publish, agentTitle, card]);

	const handleDismiss = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissRovoAgentCardButton',
				source: ANALYTICS_SOURCE,
				attributes: getAnalyticsAttributes(card),
			},
		}).fire();
		onDismiss?.();
	}, [createAnalyticsEvent, onDismiss, card]);

	return (
		<section css={[cardStyles, layoutStyles]} data-testid={`rovo-agent-card-${card.key}`}>
			<div css={[affordanceBaseStyles, dismissButtonStyles]}>
				<IconButton
					appearance="subtle"
					spacing="compact"
					icon={CrossIcon}
					onClick={handleDismiss}
					label={intl.formatMessage(i18n.dismissCardLabel, {
						agentTitle,
					})}
				/>
			</div>
			<div css={[bodyStyles, bodyLayoutStyles[layout]]}>
				<div css={headerRowStyles}>
					<img css={iconImageStyles} src={card.icon} alt="" aria-hidden />
					<Text weight="bold" color="color.text">
						<FormattedMessage {...card.title} />
					</Text>
				</div>
				<Text size="small" color="color.text.subtle">
					<FormattedMessage {...card.description} />
				</Text>
				<button
					type="button"
					css={[ctaRowStyles, affordanceBaseStyles]}
					data-testid={`rovo-agent-cta-${card.key}`}
					onClick={handleTryNow}
					aria-label={intl.formatMessage(i18n.tryItCtaLabel, { agentTitle })}
				>
					<FormattedMessage {...i18n.tryItCta} />
					<ArrowRightIcon label="" size="small" color="currentColor" />
				</button>
			</div>
			<div
				css={[previewWrapperStyles, previewLayoutStyles[layout]]}
				data-testid={`rovo-agent-preview-${card.key}`}
				aria-hidden
			>
				<img
					css={[previewImageStyles, previewImageLayoutStyles[layout]]}
					src={card.illustrations[layout]}
					alt=""
					data-testid={`rovo-agent-preview-image-${card.key}`}
				/>
			</div>
		</section>
	);
};
